import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import type { Lot, Portfolio, PortfolioNodeDesignationEnum, SimpleFund } from 'venn-api';
import { type DropMenuItem, GetColor, Icon, SimpleMenuItem } from 'venn-ui-kit';
import { useIsLeverageShortEnabled } from '../hooks/leverageAndShort';
import { ActionsButton, GreyIcon } from './actions/common';
import { compact, isNil } from 'lodash';
import { CASH_BORROWING, CASH_HOLDING } from '../modals/data-uploader/views/review/helpers';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { assertNotNil, useHasFF } from 'venn-utils';
import { SingleProxyPickerContext } from '../contexts';
import { analysisViewRefIdState } from 'venn-state';
import { useRecoilValue } from 'recoil';
import { TaxInfoModal } from './tax/TaxInfoModal';

type FundActionsProps = {
  node: Portfolio;
  originalFund: SimpleFund | undefined;
  onDelete: () => void;
  onSetDesignation: (designation: PortfolioNodeDesignationEnum) => void;
  onUpdateLots: (lots: Lot[]) => void;
};

const getCashDesignationOptionLabel = (isCashNode: boolean, isNegative: boolean) => {
  const cashHoldingOrBorrowing = isNegative ? CASH_BORROWING : CASH_HOLDING;
  if (!isCashNode) {
    return `Designate as ${cashHoldingOrBorrowing}`;
  }
  return `Remove ${cashHoldingOrBorrowing} Designation`;
};

type MenuActionOption = DropMenuItem<() => void>;
type UseOptionsProps = {
  isLeverageShortEnabled: boolean;
  onOpenTaxModal: () => void;
} & FundActionsProps;
const useOptions = ({
  node,
  originalFund,
  onDelete,
  onSetDesignation,
  isLeverageShortEnabled,
  onOpenTaxModal,
}: UseOptionsProps): MenuActionOption[] => {
  const hasProxyVisibilityFF = useHasFF('proxy_visibility_ff');
  const hasTaxImpact = useHasFF('tax_impact_ff');
  const isStudioOrReport = !isNil(useRecoilValue(analysisViewRefIdState));
  const { open: openProxyPicker } = useContext(SingleProxyPickerContext);
  const isCashNode = node.designation === 'CASH';
  const isNegative = (node.netAssets ?? 0) < 0;
  const hasTaxInfo = node.lots !== undefined && node.lots.length > 0;

  return compact([
    ...(hasProxyVisibilityFF &&
    isStudioOrReport &&
    !isCashNode &&
    node?.fund?.assetType !== 'OPTION' &&
    node?.fund?.assetType !== 'BENCHMARK'
      ? [
          {
            label: 'Add/Modify a Returns Proxy',
            value: () => openProxyPicker('returnsProxy', assertNotNil(node.fund)),
          },
          {
            label: 'Add/Modify a Holdings Proxy',
            value: () => openProxyPicker('holdingsProxy', assertNotNil(node.fund)),
          },
        ]
      : []),
    isLeverageShortEnabled && (!isCashNode || node.fund || originalFund)
      ? {
          label: getCashDesignationOptionLabel(isCashNode, isNegative),
          value: () => {
            if (isCashNode) {
              onSetDesignation('DEFAULT');
            } else {
              onSetDesignation('CASH');
            }
          },
          destructive: isCashNode,
        }
      : null,
    hasTaxImpact && isLeverageShortEnabled && (!isCashNode || node.fund || originalFund)
      ? {
          label: hasTaxInfo ? 'Edit tax information' : 'Add tax information',
          value: onOpenTaxModal,
        }
      : null,
    {
      label: isCashNode && isLeverageShortEnabled ? 'Remove cash position' : 'Remove investment',
      value: onDelete,
      destructive: true,
    },
  ]);
};

export const FundActions = ({ node, originalFund, onDelete, onSetDesignation, onUpdateLots }: FundActionsProps) => {
  const isLeverageShortEnabled = useIsLeverageShortEnabled();
  const [open, setOpen] = useState(false);
  const [taxModalOpen, setTaxModalOpen] = useState(false);

  const options = useOptions({
    node,
    originalFund,
    onDelete,
    onSetDesignation,
    onUpdateLots,
    isLeverageShortEnabled,
    onOpenTaxModal: () => setTaxModalOpen(true),
  });

  if (options.length <= 1) {
    return (
      <FundActionsButton onClick={onDelete}>
        <BlackIcon iconLabel="delete fund icon" type="trash" />
      </FundActionsButton>
    );
  }

  return (
    <DropdownMenu modal={false} open={open} onOpenChange={setOpen}>
      {taxModalOpen && <TaxInfoModal node={node} onClose={() => setTaxModalOpen(false)} onSave={onUpdateLots} />}
      <DropdownMenuTrigger asChild data-testid="qa-strategy-actions" className="qa-strategy-actions">
        <ActionsButton
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen(!open);
          }}
        >
          <GreyIcon iconLabel="strategy actions icon" type="ellipsis-h" />
        </ActionsButton>
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent align="start" onCloseAutoFocus={(e) => e.preventDefault()}>
          {options.map((item) => (
            <DropdownMenuItem
              key={item.label}
              className="!px-0 !py-0"
              onPointerLeave={(e) => e.preventDefault()}
              onPointerMove={(e) => e.preventDefault()}
              onClick={() => {
                !item.disabled && item?.value();
                setOpen(false);
              }}
            >
              <SimpleMenuItem item={item} highlighted={false} selected={false} />
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
};

const FundActionsButton = styled(ActionsButton)`
  i {
    opacity: 0;
  }
`;

const BlackIcon = styled(Icon)`
  color: ${GetColor.Black};

  &:hover {
    color: ${GetColor.Primary.Main};
  }
`;
